import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { NumberInput } from '@/components/ui/number-input';
import { Combobox } from '@/components/ui/combobox';
import { trpc } from '@/lib/trpc';
import { showSuccessToast } from '@/hooks/use-mutation-with-toast';
import { accountErrorKey } from './account-form-model';

interface TravelAddMovementDialogProps {
  purchase?: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  // When opened from a per-cari ledger the counterparty is fixed.
  fixedCari?: { counterpartyName: string } & (
    | { cariCode: string }
    | { source: 'customer' | 'partner'; id: string }
  );
}

const CURRENCIES = ['TRY', 'EUR', 'GBP', 'USD'] as const;
const ISLEM_TIPI_LABELS: Record<string, string> = {
  GELIR_GIDER: 'Gelir/Gider',
  NAKIT: 'Nakit',
  BANKA: 'Banka',
  KK_TAHSILAT: 'KK Tahsilat',
  KK_ODEME: 'KK Ödeme',
  ALINAN_CEK: 'Alınan Çek',
  KK_ONLINE: 'KK Online',
};
const selectClass =
  'h-8 w-full border border-border bg-background px-2.5 text-sm focus:outline-none focus:border-foreground focus:ring-1 focus:ring-foreground/20';

export function TravelAddMovementDialog({
  open,
  purchase = false,
  onOpenChange,
  fixedCari,
}: TravelAddMovementDialogProps) {
  const { t } = useTranslation('accounting');
  const { t: cariText } = useTranslation('travelCari');
  const today = new Date().toISOString().slice(0, 10);

  const [cardValue, setCardValue] = useState(''); // `${source}:${id}`
  const [date, setDate] = useState(today);
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [vatRate, setVatRate] = useState<number | undefined>(0);
  const [description, setDescription] = useState('');
  const [currency, setCurrency] = useState<string>('TRY');
  const [direction, setDirection] = useState<'debit' | 'credit'>(purchase ? 'credit' : 'debit');
  const [amount, setAmount] = useState<number | undefined>(undefined);
  const [tryValue, setTryValue] = useState<number | undefined>(undefined);
  const [islemCode, setIslemCode] = useState(''); // chosen İşlem (contra) account code
  const [discount, setDiscount] = useState<number | undefined>(undefined);

  const isTRY = currency === 'TRY';
  const utils = trpc.useUtils();

  // Card picker — only needed on the global page (no fixed counterparty).
  const { data: cards } = trpc.travelCari.cards.useQuery(
    { all: true },
    { enabled: open && !fixedCari }
  );
  const cardOptions = (cards?.items ?? [])
    .filter((c) => !purchase || c.source === 'partner')
    .map((c) => ({
      value: `${c.source}:${c.id}`,
      label: c.legacyCariCode ? `${c.name} · ${c.legacyCariCode}` : c.name,
      description: t(`cari.kartlar.types.${c.type}`, c.type),
      keywords: [c.name, c.legacyCariCode ?? ''],
    }));

  // İşlem (contra) account picker — the accountant's "Hesap Tanımları".
  const { data: islemAccounts } = trpc.travelCari.islemHesaplari.useQuery(undefined, {
    enabled: open,
  });
  const { data: expenses } = trpc.travelCari.purchaseExpenseAccounts.useQuery(undefined, {
    enabled: open && purchase,
  });
  const islemOptions = (islemAccounts ?? []).map((a) => ({
    value: a.code,
    label: a.name,
    description: `${ISLEM_TIPI_LABELS[a.islemTipi ?? ''] ?? a.islemTipi ?? ''} · ${a.code}`,
    keywords: [a.code, a.name, ISLEM_TIPI_LABELS[a.islemTipi ?? ''] ?? ''],
  }));
  const selectedIslem = (islemAccounts ?? []).find((a) => a.code === islemCode) ?? null;
  const showDiscount =
    !purchase && selectedIslem?.islemTipi === 'GELIR_GIDER' && !!selectedIslem?.defaultIskontoCode;

  const reset = () => {
    setCardValue('');
    setDate(today);
    setInvoiceNumber('');
    setVatRate(0);
    setDescription('');
    setCurrency('TRY');
    setDirection(purchase ? 'credit' : 'debit');
    setAmount(undefined);
    setTryValue(undefined);
    setIslemCode('');
    setDiscount(undefined);
  };

  const create = trpc.travelCari.createMovement.useMutation({
    onSuccess: () => {
      utils.travelCari.movements.invalidate();
      utils.travelCari.allMovements.invalidate();
      utils.travelCari.balances.invalidate();
      utils.travelCari.cariMizan.invalidate();
      utils.travelCari.fiscalYears.invalidate();
      utils.travelCari.accounts.invalidate();
      utils.travelCari.cards.invalidate();
      showSuccessToast(t('cari.movementCreated', 'Movement added'));
      reset();
      onOpenChange(false);
    },
  });

  const hasCounterparty = !!fixedCari || !!cardValue;
  const canSubmit =
    hasCounterparty &&
    (!purchase ||
      (!!invoiceNumber.trim() &&
        !!islemCode &&
        vatRate !== undefined &&
        vatRate >= 0 &&
        vatRate <= 100 &&
        (isTRY || (tryValue ?? 0) > 0))) &&
    !!date &&
    Number.isFinite(new Date(date).getTime()) &&
    !!amount &&
    Number.isFinite(amount) &&
    amount > 0 &&
    (isTRY || (Number.isFinite(tryValue ?? 0) && (tryValue ?? 0) >= 0));

  const submit = () => {
    if (!canSubmit || !amount || create.isPending) return;
    const [source, id] = cardValue.split(':');
    create.mutate({
      ...(fixedCari
        ? 'cariCode' in fixedCari
          ? { cariCode: fixedCari.cariCode }
          : { source: fixedCari.source, id: fixedCari.id }
        : { source: source as 'customer' | 'partner', id }),
      date: new Date(date),
      purchaseInvoice: purchase
        ? { number: invoiceNumber.trim(), vatRate: vatRate ?? 0 }
        : undefined,
      description: description || undefined,
      currency: currency as 'TRY' | 'EUR' | 'GBP' | 'USD',
      direction: purchase ? 'credit' : direction,
      amount,
      tryValue: isTRY ? amount : (tryValue ?? 0),
      contraAccountCode: islemCode || undefined,
      discountTry: showDiscount ? discount || undefined : undefined,
    });
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (create.isPending) return;
        if (!o) {
          reset();
          create.reset();
        }
        onOpenChange(o);
      }}
    >
      <DialogContent closeDisabled={create.isPending}>
        <DialogHeader>
          <DialogTitle>
            {purchase ? t('cari.addPurchaseInvoice') : t('cari.addMovement', 'Add Movement')}
            {fixedCari ? ` — ${fixedCari.counterpartyName}` : ''}
          </DialogTitle>
        </DialogHeader>

        <fieldset disabled={create.isPending} className="min-w-0 space-y-4">
          {!fixedCari && (
            <div className="space-y-1.5">
              <Label className="text-xs">{t('cari.selectCari', 'Cari')}</Label>
              <Combobox
                options={cardOptions}
                value={cardValue}
                onValueChange={setCardValue}
                placeholder={t('cari.selectCariPlaceholder', 'Select a cari card...')}
                searchPlaceholder={t('cari.searchPlaceholder', 'Name or cari code...')}
                emptyText={t('cari.noResults', 'No cari found')}
              />
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs">{t('cari.date', 'Date')}</Label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">{t('cari.voucherNo', 'Voucher No')}</Label>
              <Input readOnly value={t('cari.automaticVoucher')} />
            </div>
          </div>

          {purchase && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>{t('cari.purchaseInvoiceNumber')}</Label>
                <Input
                  value={invoiceNumber}
                  maxLength={100}
                  onChange={(e) => setInvoiceNumber(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label>{t('cari.purchaseVatRate')}</Label>
                <NumberInput value={vatRate ?? ''} onValueChange={setVatRate} decimalScale={2} />
              </div>
              <p className="text-muted-foreground col-span-2 text-xs">
                {t('cari.purchaseGrossHint')}
              </p>
            </div>
          )}
          <div className="space-y-1.5">
            <Label className="text-xs">{t('cari.description', 'Description')}</Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs">{t('cari.currency', 'Currency')}</Label>
              <select
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                className={selectClass}
              >
                {CURRENCIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">{t('cari.direction', 'Type')}</Label>
              <select
                disabled={purchase}
                value={purchase ? 'credit' : direction}
                onChange={(e) => setDirection(e.target.value as 'debit' | 'credit')}
                className={selectClass}
              >
                <option value="debit">{t('cari.debitOption', 'Debit (Borç)')}</option>
                <option value="credit">{t('cari.creditOption', 'Credit (Alacak)')}</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs">
                {t('cari.amount', 'Amount')} ({currency})
              </Label>
              <NumberInput
                value={amount ?? ''}
                onValueChange={setAmount}
                decimalScale={2}
                placeholder="0.00"
              />
            </div>
            {!isTRY && (
              <div className="space-y-1.5">
                <Label className="text-xs">{t('cari.tryValue', 'TL Value (₺)')}</Label>
                <NumberInput
                  value={tryValue ?? ''}
                  onValueChange={setTryValue}
                  decimalScale={2}
                  placeholder="0.00"
                />
              </div>
            )}
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">{purchase ? t('cari.purchaseExpenseAccount') : t('cari.islem', 'İşlem / Hesap')}</Label>
            <Combobox
              options={
                purchase
                  ? (expenses ?? []).map((a) => ({ value: a.code, label: `${a.code} · ${a.name}` }))
                  : islemOptions
              }
              value={islemCode}
              onValueChange={setIslemCode}
              placeholder={t('cari.islemPlaceholder', 'İşlem tipi / hesap seçin (opsiyonel)…')}
              searchPlaceholder={t('cari.islemSearch', 'Hesap adı / kodu…')}
              emptyText={t('cari.noResults', 'Kayıt yok')}
            />
            <p className="text-muted-foreground text-[11px]">
              {purchase
                ? t('cari.purchaseGrossHint')
                : t(
                    'cari.islemHint',
                    'Seçilirse hareket ilgili muhasebe hesabına çift taraflı işlenir (Kasa/Banka/POS/Gelir). Boş bırakılırsa yalnızca cari bakiyesine yazılır.'
                  )}
            </p>
          </div>

          {showDiscount && (
            <div className="space-y-1.5">
              <Label className="text-xs">{t('cari.discount', 'İskonto (₺)')}</Label>
              <NumberInput
                value={discount ?? ''}
                onValueChange={setDiscount}
                decimalScale={2}
                placeholder="0.00"
              />
            </div>
          )}

          <p className="text-muted-foreground text-xs">
            {t(
              'cari.movementHint',
              'Enter the amount on the chosen side — Debit (Borç) increases what they owe us, Credit (Alacak) what we owe them.'
            )}
          </p>
        </fieldset>

        {create.error && (
          <p role="alert" className="text-destructive text-sm">
            {purchase
              ? create.error.message
              : cariText(`account.errors.${accountErrorKey(create.error)}`)}
          </p>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            disabled={create.isPending}
            onClick={() => {
              reset();
              create.reset();
              onOpenChange(false);
            }}
          >
            {t('cari.cancel', 'Cancel')}
          </Button>
          <Button onClick={submit} disabled={create.isPending || !canSubmit}>
            {create.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t('cari.save', 'Save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
